import moment from 'moment'
import React from 'react'
import { trim, split, filter, map, includes, template } from 'lodash'
import i18n from 'simple-react-i18n'
import { hasValue, round } from './NumberUtil'
import { QUALITO_IMPORT_TYPE } from '../quality/components/qualitometers/addQualito/AddQualitoConstants'
import cronstrue from 'cronstrue/i18n'
import { getCurrentLang } from './LangUtils'
import { getFullDate } from './DateUtil'
import { PIEZO_IMPORT_TYPE } from '../piezometry/components/piezometers/addPiezo/AddPiezoConstants'
import { getObjectLabel } from './StoreUtils'
import { HYDRO_IMPORT_TYPE } from 'hydrometry/constants/HydrometryConstants'
import { PLUVIO_IMPORT_TYPE } from 'pluviometry/constants/PluviometryConstant'
import { IMPORT_FORMATS } from '../import/constants/JobFormat'
import { Grid, Icon, Tooltip, Stack, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { rotate } from './AnimationsUtils'
import WarningIcon from '@mui/icons-material/Warning'
import {
    DATA_ADD_LAST_DATA,
    DATA_ADD_NEW_DATA,
    DATA_REPLACE_DATA,
    DATA_REPLACE_HISTO,
    IMPORT_TYPE,
} from '../traitements/constants/TraitementConstants'
import { getGeneralLevelling } from './SettingUtils'

const getExecStatus = status => {
    if (status) {
        return i18n.success
    } else if (status === false) {
        return i18n.error
    }
    return i18n.running
}

const getImportMode = value => {
    switch (value) {
        case 'dataIntegration':
            return 'DATA_ADD_LAST_DATA'
        case 'dataFullIntegration':
            return 'DATA_ADD_NEW_DATA'
        case 'dataNothing':
            return ''
        default:
            return value
    }
}

const getExecStatusColor = (execution) => {
    switch (execution.status ?? execution.statusCode) {
        case 0:
            return 'red'
        case 1:
            return 'green'
        case 2:
            return 'green'
        case 3:
            return 'orange'
        case 4:
            return 'grey'
        case 5:
            return 'orange'
        case 6:
            return 'blue'
        case 7:
            return 'blue'
        case 8:
            return 'orange'
        case 9:
            return 'orange'
        case 10:
            return 'orange'
        default:
            if (execution.date < moment().subtract(3, 'days').valueOf()) {
                return 'red'
            }
            return 'blue'
    }
}

// Nom de l'icône Material Icons correspondant au statut d'exécution.
// Source unique réutilisée par getExecStatusIcon et par les rendus non-React (ex. symboles ECharts).
const getExecStatusIconName = (execution) => {
    switch (execution.status ?? execution.statusCode) {
        case -1:
            return 'sync'
        case 0:
            return 'error'
        case 1:
            return 'done'
        case 2:
            return 'done'
        case 3:
            return 'pan_tool'
        case 4:
            return 'local_cafe'
        case 5:
            return 'live_help'
        case 6:
            return 'more_horiz'
        case 7:
            return 'pan_tool'
        case 8:
            return 'hourglass_full'
        case 9:
            return 'sync_problem'
        case 10:
            return 'hide_source'
        default:
            if (execution.date < moment().subtract(3, 'days').valueOf()) {
                return 'error'
            }
            return 'sync'
    }
}

const getExecStatusIcon = (execution, size = 50, tooltipContent = null) => {
    const icon = getExecStatusIconName(execution)
    const color = getExecStatusColor(execution)

    if (execution.status === 2) {
        return (
            <Tooltip title={tooltipContent}>
                <Grid sx={{ width: `${size}px` }}>
                    <Icon
                        sx={{ color, fontSize: `${size}px`, position: 'relative', animation: icon === 'sync' ? `${rotate} 2s linear infinite` : '' }}
                    >{icon}</Icon>
                    <WarningIcon
                        sx={{ color: 'orange', right: 10, fontSize: `${round(size / 2, 0)}px`, position: 'relative', animation: icon === 'sync' ? `${rotate} 2s linear infinite` : '' }}
                    />
                </Grid>
            </Tooltip>
        )
    }

    return (
        <Tooltip title={tooltipContent}>
            <Icon
                id={execution.status === 1 && 'success' || hasValue(execution.status) && 'error' || 'jobInProgress'}
                sx={{ color, fontSize: `${size}px`, width: `${size}px`, height: `${size}px`, animation: icon === 'sync' ? `${rotate} 2s linear infinite reverse` : '' }}
            >
                {icon}
            </Icon>
        </Tooltip>
    )
}

const getExecStatusLabel = (execution) => {
    switch (execution.status ?? execution.statusCode) {
        case 0:
            return i18n.error
        case 1:
            return i18n.success
        case 2:
            return i18n.successWithWarnings
        case 3:
            return i18n.abortedManually
        case 4:
            return i18n.nothingToDoOrProcess
        case 5:
            return i18n.noInformation
        case 6:
            return i18n.unknownJobStatus
        case 7:
            return (i18n.interruptServerMaintenance)
        case 8:
            return i18n.jobNotExecuted
        case 9:
            return i18n.jobTooBusy2
        case 10:
            return i18n.missingDataJob
        default:
            if (execution.date < moment().subtract(3, 'days').valueOf()) {
                return i18n.error
            }
            return i18n.inProgress
    }
}

const getExecTypeIcon = (cron, size = 25) => {
    if (cron) {
        return <i className='material-icons ' style={{ fontSize: `${size}px` }}>android</i>
    }
    return null
}

const getMeasureCoteList = () => [
    { code: 2, name: i18n.depth },
    { code: 1, name: getGeneralLevelling() },
]

const getIntegrationModes = () => [
    {
        value: '',
        label: i18n.noDataIntegration,
    },
    {
        value: DATA_ADD_LAST_DATA,
        label: i18n.lastDataIntegration,
    },
    {
        value: DATA_ADD_NEW_DATA,
        label: i18n.addOnlyNewsDataIntegration,
    },
    {
        value: DATA_REPLACE_HISTO,
        label: i18n.replaceAllHistory,
    },
    {
        value: DATA_REPLACE_DATA,
        label: i18n.replaceDataIntegration,
    },
]

const getIntegrationModesOpenDataQuality = () => [...getIntegrationModes(), {
    value: 'DATA_REPLACE_RECENT_HISTO',
    label: i18n.replaceRecentHisto,
}]

const getIntegrationModesOpenData = () => getIntegrationModes().filter(mode => mode.value !== DATA_ADD_NEW_DATA && mode.value !== DATA_REPLACE_DATA)

const getJobStatus = () => [
    {
        header: 'statut-0',
        label: i18n.error,
    },
    {
        header: 'statut-1',
        label: i18n.success,
        id: 'succesLogo',
    },
    {
        header: 'statut-2',
        label: i18n.successWithWarnings,
    },
    {
        header: 'statut-3',
        label: i18n.abortedManually,
    },
    {
        header: 'statut-4',
        label: i18n.nothingToDoOrProcess,
    },
    {
        header: 'statut-5',
        label: i18n.noInformation,
    },
    {
        header: 'statut-6',
        label: i18n.unknownJobStatus,
    },
    {
        header: 'statut-7',
        label: i18n.interruptServerMaintenance,
    },
    {
        header: 'statut-8',
        label: i18n.jobNotExecuted,
    },
    {
        header: 'statut-10',
        label: i18n.missingDataJob,
    },
    {
        header: 'statut--1',
        label: i18n.inProgress,
    },
]

const getManualJobId = stationType => {
    switch (stationType) {
        case QUALITO_IMPORT_TYPE.ADES_QUALITO:
            return -1000
        case QUALITO_IMPORT_TYPE.NAIADES:
            return -1001
        case PIEZO_IMPORT_TYPE.ADES_PIEZO:
            return -1002
        case HYDRO_IMPORT_TYPE.HYDRO_PORTAIL:
            return -1003
        case PLUVIO_IMPORT_TYPE.METEO_FRANCE:
            return -1006
        case QUALITO_IMPORT_TYPE.OROBNAT:
            return -1007
        case IMPORT_TYPE.TIDE_GAUGE:
            return -1008
        default:
            return 0
    }
}

const RELATIVE_MONTHLY_RANGES = {
    '1-7': 'firstOccurrence',
    '8-14': 'secondOccurrence',
    '15-21': 'thirdOccurrence',
    '22-28': 'fourthOccurrence',
    '25-31': 'lastOccurrence',
}

const WEEKDAY_NAMES_FR = {
    '1': 'lundi',
    '2': 'mardi',
    '3': 'mercredi',
    '4': 'jeudi',
    '5': 'vendredi',
    '6': 'samedi',
    '0': 'dimanche',
    '7': 'dimanche',
}

const formatSingleCron = (cron) => {
    if (!cron) return ''
    try {
        const parts = split(trim(cron), /\s+/)
        if (parts.length === 7) {
            const min = parts[1]
            const hour = parts[2]
            const dayOfMonth = parts[3]
            const dayOfWeek = parts[5]

            if (RELATIVE_MONTHLY_RANGES[dayOfMonth] && WEEKDAY_NAMES_FR[dayOfWeek]) {
                const occKey = RELATIVE_MONTHLY_RANGES[dayOfMonth]
                const occTextRaw = i18n[occKey] || occKey
                const occText = typeof occTextRaw === 'string' ? occTextRaw.toLowerCase() : ''
                const dayText = WEEKDAY_NAMES_FR[dayOfWeek]
                const formattedHour = hour === '*' ? '*' : (hour.length < 2 ? `0${hour}` : hour)
                const formattedMin = includes(min, '/') || min === '*' ? '00' : (min.length < 2 ? `0${min}` : min)
                return `Le ${occText} ${dayText} du mois à ${formattedHour}:${formattedMin}`
            }
        }
        return cronstrue.toString(cron, { locale: getCurrentLang()[0] })
    } catch (e) {
        return cron
    }
}

const formatCronList = (cronStr) => {
    if (!cronStr) return []
    return map(filter(map(split(cronStr, ';'), c => trim(c)), Boolean), c => formatSingleCron(c))
}

const renderProgrammationCell = (cronStr) => {
    if (!cronStr) return { value: '', text: '' }
    const list = formatCronList(cronStr)
    if (list.length === 0) {
        return { value: '', text: '' }
    }
    if (list.length === 1) {
        return { value: list[0], text: list[0] }
    }
    const label = template(i18n.nbSchedules)({ count: list.length })
    const tooltipContent = (
        <Stack spacing={0.5} sx={{ p: 0.5 }}>
            {list.map((desc, idx) => (
                <Typography key={idx}>• {desc}</Typography>
            ))}
        </Stack>
    )
    return {
        value: (
            <Tooltip title={tooltipContent} arrow>
                <Typography sx={{ cursor: 'pointer', textDecoration: 'underline dotted' }}>
                    {label}
                </Typography>
            </Tooltip>
        ),
        text: label,
        list,
    }
}

const getJobList = (jobs, contributorsIndex = {}) => {
    const headers = ['jobId', 'jobExecutionId', 'jobType', 'jobName', 'status', 'date', 'user', 'administrator', 'programmation', 'nullValue', 'nullValue2', 'nullValue3']
    const tableData = jobs.map(job => {
        const statusIcon = getExecStatusIcon(job, 30)
        const progCell = renderProgrammationCell(job.cron)
        return {
            id: job.jobId,
            nullValue: {
                value: getExecTypeIcon(job.cron, 30),
                color: 'white',
                setTooltip: () => i18n.jobScheduled,
            },
            nullValue2: {
                value: job.jobPreviousId ? <ArrowBackIcon /> : null,
                setTooltip: job.jobPreviousId ? (() => {
                    const found = jobs.find(j => j.jobId === job.jobPreviousId)
                    return found ? `${i18n.previous} : ${found.jobName}` : ''
                }) : null,
            },
            nullValue3: {
                value: job.jobNextId ? <ArrowForwardIcon /> : null,
                setTooltip: job.jobNextId ? (() => {
                    const found = jobs.find(j => j.jobId === job.jobNextId)
                    return found ? `${i18n.next} : ${found.jobName}` : ''
                }) : null,
            },
            programmation: {
                value: progCell.value,
                sortValue: progCell.text,
            },
            jobId: {
                value: job.jobId,
                color: 'white',
            },
            jobExecutionId: {
                value: job.jobExecutionId,
                color: 'white',
            },
            jobType: {
                value: IMPORT_FORMATS().find(i => i.value === job.jobType)?.label ?? job.jobType,
                color: 'white',
            },
            jobName: {
                value: job.jobName,
                color: 'white',
            },
            administrator: {
                value: hasValue(job.managers) ? getObjectLabel(contributorsIndex[job.managers?.split(';')?.[0]] ?? {}, 'mnemonique') : undefined,
                color: 'white',
            },
            status: {
                value: statusIcon,
                color: 'white',
                setTooltip: () => (
                    <div className='padding-left-1 padding-right-1 valign-wrapper'>
                        {statusIcon}
                        <span className='margin-left-1'>{getExecStatusLabel(job)}</span>
                    </div>
                ),
            },
            date: {
                value: getFullDate(job.date),
                color: 'white',
            },
            user: {
                value: job.login,
                color: 'white',
            },
            job,
        }
    })
    return {
        type: {
            headers,
        },
        jobs: tableData,
    }
}


const getJobListNewTable = (jobs, contributorsIndex = {}) => {
    const headers = ['jobId', 'jobExecutionId', 'jobType', 'jobName', 'status', 'date', 'user', 'administrator', 'programmation', 'nullValue', 'nullValue2', 'nullValue3']
    const tableData = jobs.map(job => {
        const statusIcon = getExecStatusIcon(job, 30)
        const progCell = renderProgrammationCell(job.cron)
        const tooltipPrevious = job.jobPreviousId
            ? (() => {
                const found = jobs.find(j => j.jobId === job.jobPreviousId)
                return found ? `${i18n.previous} : ${found.jobName}` : ''
            })()
            : ''
        const tooltipNext = job.jobNextId
            ? (() => {
                const found = jobs.find(j => j.jobId === job.jobNextId)
                return found ? `${i18n.next} : ${found.jobName}` : ''
            })()
            : ''
        return {
            id: job.jobId,
            nullValue: {
                value: <Tooltip title={i18n.jobScheduled}>{getExecTypeIcon(job.cron, 30)}</Tooltip>,
            },
            nullValue2: {
                value: job.jobPreviousId ? <Tooltip title={tooltipPrevious}><ArrowBackIcon /></Tooltip> : null,
            },
            nullValue3: {
                value: job.jobNextId ? <Tooltip title={tooltipNext}><ArrowForwardIcon /></Tooltip> : null,
            },
            programmation: {
                value: progCell.value,
            },
            jobId: {
                value: job.jobId,
            },
            jobExecutionId: {
                value: job.jobExecutionId,
            },
            jobType: {
                value: IMPORT_FORMATS().find(i => i.value === job.jobType)?.label ?? job.jobType,
            },
            jobName: {
                value: job.jobName,
            },
            administrator: {
                value: hasValue(job.managers) ? getObjectLabel(contributorsIndex[job.managers?.split(';')?.[0]] ?? {}, 'mnemonique') : undefined,
            },
            status: {
                value: <Tooltip title={getExecStatusLabel(job)}>{statusIcon}</Tooltip>,
            },
            date: {
                value: getFullDate(job.date),
            },
            user: {
                value: job.login,
            },
            job,
        }
    })
    return {
        type: {
            headers,
        },
        jobs: tableData,
    }
}

const FILTERS = 'FILTERS'
const STATIONS = 'STATIONS'

const getStationModes = () => [
    { code: FILTERS, name: i18n.selectByCriteria },
    { code: STATIONS, name: i18n.selectStations },
]

export {
    getExecStatusIcon, getExecStatusIconName, getImportMode, getExecStatusColor, getExecStatus, getExecTypeIcon, getMeasureCoteList, getIntegrationModes,
    getExecStatusLabel, getJobStatus, getManualJobId, getJobList, getIntegrationModesOpenDataQuality, getIntegrationModesOpenData, getStationModes, FILTERS, STATIONS, getJobListNewTable,
    formatSingleCron, formatCronList, renderProgrammationCell,
}
